/* RESPONSIBLE TEAM: team-frontend-tech */

/*
 * ⚠️  WARNING: BRIDGE LOGIC - DO NOT MODIFY WITHOUT #team-frontend-tech APPROVAL ⚠️
 *
 * This service contains critical bridge logic that enables communication between the
 * Ember app (apps/embercom) and React app (apps/teammate-app). Any modifications
 * to this service can break the integration between the two applications.
 *
 * Before making any changes to this file, please reach out to #team-frontend-tech
 * for review and approval.
 */

import Service from '@ember/service';
import { action } from '@ember/object';
import { tracked } from 'tracked-built-ins';
import { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';
import type NavbarCollapsingService from 'embercom/services/navbar-collapsing-service';
import type ThemeService from 'embercom/services/theme-service';
import type Theme from 'embercom/objects/inbox/types/theme';
import type Snackbar from 'embercom/services/snackbar';
import { isReactRoute } from 'embercom/lib/react-route';
import Metrics from 'embercom/models/metrics';
// @ts-ignore
import { globalRef } from 'ember-ref-bucket';
import type Transition from '@ember/routing/transition';
import ENV from 'embercom/config/environment';

interface AppState {
  navbarCollapsed: boolean;
  locale: string;
  theme: Theme;
}

const MODAL_COMPONENT_BRIDGE_MAP = {
  'web-push-notifications-permission-denied': 'account/notifications-denied-modal',
  'web-push-notifications-permission-prompt': 'account/notifications-ask-modal',
};

type BridgeEventName =
  | 'route:boot'
  | 'route:change'
  | 'navbar:toggle'
  | 'navbar:collapse'
  | 'notification:default'
  | 'notification:confirmation'
  | 'notification:help'
  | 'notification:error'
  | 'notification:warning'
  | 'notification:loading'
  | 'modal:open'
  | 'modal:close'
  | 'paywall:open'
  | 'analytics:track'
  | 'metrics:capture'
  | 'permissionsModal:open'
  | 'teammate-app:event'
  | 'mounting-point:inserted'
  | 'snackbar:notify';

export default class ReactTeammateAppIslandService extends Service {
  @tracked reactTeammateApp?: typeof window.ReactTeammateApp;
  @tracked highlightReactComponents = false;
  loadedViteScripts = false;
  loadedEntryPointScript = false;

  @globalRef('main-content-well') declare mainContentWell: HTMLElement;
  @globalRef('react-container') declare reactContainerDiv: HTMLElement;

  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare snackbar: Snackbar;
  @service declare modalService: $TSFixMe;
  @service declare navbarCollapsingService: NavbarCollapsingService;
  @service declare themeService: ThemeService;
  @service declare paywallService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;
  @service declare permissionsService: $TSFixMe;

  isAppBooted = false;
  preBootNavigationEventUrl?: string;
  private teammateAppEventSubscribers: Map<string, Array<(data: Record<string, unknown>) => void>> =
    new Map();
  private mountingEventSubscribers: Map<string, Array<(payload: any) => void>> = new Map();

  get appState(): AppState {
    return {
      navbarCollapsed: this.navbarCollapsingService.collapsed,
      locale: this.intl.primaryLocale,
      theme: this.themeService.computedTheme,
    };
  }

  get bridge() {
    return window.ReactTeammateApp?.bridge;
  }

  setupBridge() {
    this.bridge?.addEventListener('ember:bridge', (event: CustomEvent) => {
      let { type, payload } = event.detail as { type: BridgeEventName; payload: any };

      switch (type) {
        case 'route:boot':
          this.routerBootEventHandler();
          break;
        case 'route:change': {
          // Preserve experimental build params that React doesn't manage.
          // React relative navigations (navigate('..'), navigate('id')) produce empty search,
          // which causes Ember to drop application-level sticky params like teammate_app_rev.
          let EXPERIMENTAL_BUILD_PARAMS = ['ember_rev', 'teammate_app_rev'];

          let currentParams = new URLSearchParams(window.location.search);
          let newParams = new URLSearchParams(payload.search || '');

          for (let param of EXPERIMENTAL_BUILD_PARAMS) {
            if (currentParams.has(param) && !newParams.has(param)) {
              newParams.set(param, currentParams.get(param)!);
            }
          }

          let search = newParams.toString();
          let url = search ? `${payload.pathname}?${search}` : payload.pathname;
          this.router.transitionTo(url);
          break;
        }
        case 'navbar:toggle':
          this.navbarCollapsingService.toggleNavbarCollapsed();
          break;
        case 'navbar:collapse':
          this.navbarCollapsingService.collapseNavbar();
          break;
        case 'notification:default':
          this.notificationsService.notify(payload.message, payload.duration);
          break;
        case 'notification:confirmation':
          this.notificationsService.notifyConfirmation(payload.message, payload.duration);
          break;
        case 'notification:help':
          this.notificationsService.notifyHelp(payload.message, payload.duration);
          break;
        case 'notification:error':
          this.notificationsService.notifyError(payload.message, payload.duration);
          break;
        case 'notification:warning':
          this.notificationsService.notifyWarning(payload.message, payload.duration);
          break;
        case 'notification:loading':
          this.notificationsService.notifyLoading(payload.message, payload.duration);
          break;
        case 'snackbar:notify':
          this.snackbar.notify(payload.message, {
            type: payload.type,
            persistent: payload.persistent,
            clearable: payload.clearable,
            timeout: payload.timeout,
          });
          break;
        case 'modal:open': {
          let modalComponent =
            MODAL_COMPONENT_BRIDGE_MAP[payload.name as keyof typeof MODAL_COMPONENT_BRIDGE_MAP];
          this.modalService.openModal(modalComponent, payload.model, payload.options);
          break;
        }
        case 'modal:close':
          this.modalService.closeModal(payload?.results);
          break;
        case 'paywall:open':
          this.paywallService.open(payload);
          break;
        case 'analytics:track':
          this.intercomEventService.trackAnalyticsEvent(payload, true);
          break;
        case 'metrics:capture':
          Metrics.capture(payload);
          break;
        case 'permissionsModal:open':
          this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
            payload.permissionRequired,
          );
          break;
        case 'teammate-app:event':
          this.handleTeammateAppEvent(payload);
          break;
        case 'mounting-point:inserted': {
          this.handleMountingPointInserted(payload);
          break;
        }
      }
    });
  }

  dispatchBridgeEvent(type: string, payload: any) {
    if (this.isAppBooted || type === 'teammate-app:event') {
      this.bridge?.dispatchEvent(
        new CustomEvent('react:bridge', {
          detail: { type, payload },
        }),
      );
    } else if (type === 'route:navigation-request') {
      this.preBootNavigationEventUrl = payload.to;
    }
  }

  dispatchRouteNavigationRequestEvent(to: string) {
    this.dispatchBridgeEvent('route:navigation-request', { to, appState: this.appState });
  }

  dispatchAppStateChangeEvent(state: Partial<AppState>) {
    this.dispatchBridgeEvent('app:state-change', state);
  }

  dispatchTeammateAppEvent(identifier: string, data: Record<string, unknown> = {}) {
    this.dispatchBridgeEvent('teammate-app:event', { identifier, data });
  }

  /**
   * Handle teammate app events from the Teammate App
   */
  private handleTeammateAppEvent(payload: { identifier: string; data: Record<string, unknown> }) {
    let subscribers = this.teammateAppEventSubscribers.get(payload.identifier);
    if (subscribers) {
      subscribers.forEach((callback) => callback(payload.data));
    }
  }

  /**
   * Handle mounting point inserted events from React
   */
  private handleMountingPointInserted(payload: { identifier: string }) {
    let subscribers = this.mountingEventSubscribers.get(payload.identifier);
    if (subscribers && subscribers.length > 0) {
      subscribers.forEach((handler) => handler(payload));
    }
  }

  /**
   * Subscribe to teammate app events for a specific identifier
   * @param identifier - The identifier to listen for (e.g., 'ai-categories', 'usage-reminders')
   * @param callback - Function to call when the event is triggered, receives the event data
   * @returns A function to unsubscribe
   */
  subscribeToTeammateAppEvent(
    identifier: string,
    callback: (data: Record<string, unknown>) => void,
  ): () => void {
    if (!this.teammateAppEventSubscribers.has(identifier)) {
      this.teammateAppEventSubscribers.set(identifier, []);
    }

    let subscribers = this.teammateAppEventSubscribers.get(identifier)!;
    subscribers.push(callback);

    // Return unsubscribe function
    return () => {
      let index = subscribers.indexOf(callback);
      if (index > -1) {
        subscribers.splice(index, 1);
      }

      // Clean up empty subscriber arrays
      if (subscribers.length === 0) {
        this.teammateAppEventSubscribers.delete(identifier);
      }
    };
  }

  subscribeToMountingEvent(identifier: string, handler: (payload: any) => void): void {
    if (!this.mountingEventSubscribers.has(identifier)) {
      this.mountingEventSubscribers.set(identifier, []);
    }

    let handlers = this.mountingEventSubscribers.get(identifier)!;
    handlers.push(handler);
  }

  unsubscribeFromMountingEvent(identifier: string, handler: (payload: any) => void): void {
    let handlers = this.mountingEventSubscribers.get(identifier);
    if (!handlers) {
      return;
    }

    let index = handlers.indexOf(handler);
    if (index > -1) {
      handlers.splice(index, 1);
    }

    if (handlers.length === 0) {
      this.mountingEventSubscribers.delete(identifier);
    }
  }

  @action
  onAppReady() {
    if (!this.reactTeammateApp) {
      this.reactTeammateApp = window.ReactTeammateApp;
      this.setupBridge();
    }
  }

  routerBootEventHandler() {
    this.isAppBooted = true;
    if (this.preBootNavigationEventUrl) {
      this.dispatchRouteNavigationRequestEvent(this.preBootNavigationEventUrl);
      this.preBootNavigationEventUrl = undefined;
    }

    if (isReactRoute(this.router.currentRoute)) {
      this.dispatchBridgeEvent('route:boot', {
        to: `${this.router.currentURL}${window.location.hash}`,
        appState: this.appState,
      });
    }
  }

  showReactContent() {
    if (this.mainContentWell) {
      this.mainContentWell.classList.add('hidden');
    }

    if (this.reactContainerDiv) {
      this.reactContainerDiv.classList.remove('hidden');
    }
  }

  hideReactContent() {
    if (this.mainContentWell) {
      this.mainContentWell.classList.remove('hidden');
    }

    if (this.reactContainerDiv) {
      this.reactContainerDiv.classList.add('hidden');
    }
  }

  handleTransition(transition: Transition) {
    if (ENV.environment === 'test') {
      return;
    }

    let toReact = isReactRoute(transition.to);
    let toEmber = !toReact;

    if (toReact) {
      this.showReactContent();
      this.dispatchRouteNavigationRequestEvent(`${this.router.currentURL}${window.location.hash}`);

      return;
    }

    if (toEmber) {
      this.hideReactContent();
      this.dispatchRouteNavigationRequestEvent('/empty');

      return;
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    reactTeammateAppIslandService: ReactTeammateAppIslandService;
    'react-teammate-app-island-service': ReactTeammateAppIslandService;
  }
}
